// src/pages/ChatDocuments/ChatMediaPresentation.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../../shared/api/axios";
import { useAuth } from "../../../shared/contexts/AuthContext";
import { decryptMedia } from "../../../shared/chat/chatAsync";
import SmartVideoPlayerChat from "./SmartVideoPlayerChat";
import { useSocket } from "../../../shared/socket/SocketProvider";
import {
  unwrapMediaKey,
  decryptMediaFromUrlToObjectURL,
  loadMySecretKey,
  ensureMySecretKeyAvailable,
} from "../../../shared/crypto/simpleE2EE";
import { useMediaViewerOverlay } from "../Presentation/MediaViewerOverlay";
import { useTranslation } from "react-i18next";

type MediaMsg = {
  _id: string;
  messageId?: string; // for albumItems path: parent message to "close"
  media: string;
  mediaMime?: string;
  mediaIv?: string;
  mediaKeyEnvelope?: any;
  mediaKeySelfEnvelope?: any;

  // R2 “items” path
  r2Url?: string | null;
  fileNonceB64?: string | null;
  envForRecipient?: any | null;
  envForSender?: any | null;
  scheme?: "aes-gcm" | "xsalsa20poly1305" | string | null;

  decryptedMediaUrl?: string;
};

type AlbumItem = {
  id: string;
  type: "image" | "video" | string;
  r2: { key?: string; url: string };
  bytes?: number | null;
  durMs?: number | null;
  crypto?: {
    scheme?: "xsalsa20poly1305" | "aes-gcm" | string;
    fileNonceB64?: string;
    ivB64u?: string;
    mediaKeyEnvelope?: any;
    mediaKeySelfEnvelope?: any;
  };
};

function looksLikeMongoId(v: any): v is string {
  return typeof v === "string" && /^[a-f\d]{24}$/i.test(v);
}

function toB64url(s: string) {
  return s.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}
function ensureB64urlEnv(raw: any) {
  if (!raw) return null;
  const nonce = raw?.nonce ? toB64url(String(raw.nonce)) : "";
  const ciphertext = raw?.ciphertext ? toB64url(String(raw.ciphertext)) : "";
  const ephPub = raw?.ephPub ? toB64url(String(raw.ephPub)) : "";
  if (!nonce || !ciphertext || !ephPub) return null;
  return { nonce, ciphertext, ephPub };
}

function msgKey(m: MediaMsg) {
  return `${String(m.messageId || m._id)}:${String(m.media || "").slice(0, 48)}:${String(
    m.mediaIv || m.fileNonceB64 || ""
  ).slice(0, 24)}`;
}

// Posts to backend regardless of whether axios baseURL already includes "/api"
async function postClose(apiClient: any, messageId: string) {
  const base = String(apiClient.defaults?.baseURL || "").replace(/\/$/, "");
  const url = base.endsWith("/api") || base.includes("/api/") ? "/message-expiry/close" : "/api/message-expiry/close";
  await apiClient.post(url, { messageId });
}

// keepalive version for pagehide/beforeunload (with Authorization header)
function postCloseKeepalive(apiClient: any, messageId: string) {
  const base = String(apiClient.defaults?.baseURL || "").replace(/\/$/, "");
  const url =
    base && /^https?:\/\//i.test(base)
      ? `${base}${base.endsWith("/api") || base.includes("/api/") ? "" : "/api"}/message-expiry/close`
      : base.endsWith("/api") || base.includes("/api/")
        ? "/message-expiry/close"
        : "/api/message-expiry/close";

  const auth =
    (apiClient.defaults.headers as any)?.common?.Authorization ||
    (apiClient.defaults.headers as any)?.Authorization;

  return fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(auth ? { Authorization: auth } : {}),
    },
    body: JSON.stringify({ messageId }),
    keepalive: true,
  });
}

async function decryptXsalsaItemToBlobUrl(m: MediaMsg, preferSenderFirst: boolean, authToken?: string | null) {
  const envRaw = preferSenderFirst
    ? m.mediaKeySelfEnvelope ?? m.envForSender ?? m.mediaKeyEnvelope ?? m.envForRecipient
    : m.mediaKeyEnvelope ?? m.envForRecipient ?? m.mediaKeySelfEnvelope ?? m.envForSender;

  const env = ensureB64urlEnv(envRaw);
  if (!env) throw new Error("decryptXsalsaItemToBlobUrl: bad envelope");

  let mySecret = loadMySecretKey();
  if (!mySecret) {
    mySecret = await ensureMySecretKeyAvailable();
  }

  const keyB64 = unwrapMediaKey(env, mySecret);

  const ivB64 = String(m.fileNonceB64 || m.mediaIv || "");
  if (!ivB64) throw new Error("decryptXsalsaItemToBlobUrl: no nonce");

  const explicit =
    m.scheme === "aes-gcm" || m.scheme === "xsalsa20poly1305" ? m.scheme : undefined;

  const mime =
    m.mediaMime?.startsWith("video/")
      ? "video/mp4"
      : m.mediaMime?.startsWith("image/")
        ? m.mediaMime
        : "application/octet-stream";

  return decryptMediaFromUrlToObjectURL(m.r2Url || m.media, ivB64, keyB64, authToken || null, mime, explicit);
}

export default function ChatMediaPresentation() {
  const { t } = useTranslation();
  const location = useLocation();
  const nav = useNavigate();
  const { userId } = useParams<{ userId: string }>();

  const { user, token } = useAuth() as any;
  const { socket } = useSocket() as any;
  const { openOverlay, Overlay } = useMediaViewerOverlay() as any;

  // ---- navigation state ----
  const st = (location.state || {}) as any;

  const onlyIds: string[] = Array.isArray(st.onlyIds) ? st.onlyIds.map(String) : [];
  const parentId: string | undefined = st.parentId ? String(st.parentId) : undefined;
  const albumItems: AlbumItem[] = Array.isArray(st.albumItems) ? st.albumItems : [];
  const isSender: boolean = !!st.isSender;

  // ✅ IMPORTANT:
  // If you use the "albumItems" path (inline R2 items), you MUST also pass albumIds (mongo ids)
  // so we can close the real message docs that contain media/mediaKey.
  const albumIdsFromNav: string[] = Array.isArray(st.albumIds) ? st.albumIds.map(String) : [];

  const closeMessageIdFromNav: string | null =
    looksLikeMongoId(st.closeMessageId) ? String(st.closeMessageId) : null;

  // Track IDs the user actually opened (fallback only)
  const openedIdsRef = useRef<Set<string>>(new Set());

  // Ensure we only call /close once per presentation session
  const closedOnceRef = useRef(false);

  // ---- Build the list of messageIds to "close" ----
  const computeCloseTargetIds = useCallback((): string[] => {
    const candidates: string[] = [];

    // 1) explicit from nav state (if valid)
    if (closeMessageIdFromNav && looksLikeMongoId(closeMessageIdFromNav)) {
      candidates.push(closeMessageIdFromNav);
    }

    // 2) close all media message ids we know about
    //    (this is the KEY fix: closing parentId often does nothing)
    for (const id of onlyIds) if (looksLikeMongoId(id)) candidates.push(id);
    for (const id of albumIdsFromNav) if (looksLikeMongoId(id)) candidates.push(id);

    // 3) fallback: parentId (only if it’s actually a media message in your DB)
    if (parentId && looksLikeMongoId(parentId)) candidates.push(parentId);

    // 4) fallback: anything user opened
    for (const id of openedIdsRef.current) if (looksLikeMongoId(id)) candidates.push(String(id));

    // de-dupe
    return Array.from(new Set(candidates));
  }, [closeMessageIdFromNav, onlyIds, albumIdsFromNav, parentId]);

  async function postCloseManyAxios(messageIds: string[]) {
    // call sequentially to reduce backend load; still fast for small batches
    for (const id of messageIds) {
      await postClose(api, id);
    }
  }

  function postCloseManyKeepalive(messageIds: string[]) {
    // best-effort keepalive; fire them all
    messageIds.forEach((id) => {
      postCloseKeepalive(api, id).catch(() => {});
    });
  }

  const closePresentationOnce = useCallback(async () => {
    if (closedOnceRef.current) return;

    const ids = computeCloseTargetIds();
    if (!ids.length) {
      console.warn("[presentation-close] no ids -> not calling /message-expiry/close");
      return;
    }

    closedOnceRef.current = true;

    try {
      await postCloseManyAxios(ids);
      console.log("[presentation-close] close OK for:", ids);
    } catch (e) {
      console.warn("[presentation-close] close failed (non-fatal):", e);
    } finally {
      openedIdsRef.current.clear();
    }
  }, [computeCloseTargetIds]);

  // 1) Close on component unmount (normal route change)
  useEffect(() => {
    return () => {
      void closePresentationOnce();
    };
  }, [closePresentationOnce]);

  // 2) Close on tab close / app background (more reliable)
  useEffect(() => {
    const onHide = () => {
      if (closedOnceRef.current) return;

      const ids = computeCloseTargetIds();
      if (!ids.length) return;

      closedOnceRef.current = true;
      postCloseManyKeepalive(ids);
      openedIdsRef.current.clear();
    };

    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onHide);

    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onHide);
    };
  }, [computeCloseTargetIds]);

  const safeBack = useCallback(() => {
    void closePresentationOnce();

    if (!userId) {
      nav("/chat", { replace: true });
      return;
    }
    nav(`/chat/${encodeURIComponent(userId)}`, { replace: true, state: { toUserId: userId } });
  }, [nav, userId, closePresentationOnce]);

  // ---- local UI state ----
  const [items, setItems] = useState<MediaMsg[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // ...keep the rest of your file as-is...

  // helpers
  const revokeUrls = (arr: MediaMsg[]) => {
    for (const m of arr) {
      try {
        if (m.decryptedMediaUrl && m.decryptedMediaUrl.startsWith("blob:")) URL.revokeObjectURL(m.decryptedMediaUrl);
      } catch {}
    }
  };

  const isStream = (m: MediaMsg) =>
    (m.mediaMime || "").toLowerCase() === "video/cloudflare-stream" || /iframe\.videodelivery\.net/.test(m.media || "");

  const looksLikeImageUrl = (u: string) =>
    /\.(jpe?g|png|gif|webp|bmp|tiff?|heic|heif)(\?.*)?$/i.test(u || "");

  const isVideoMime = (m?: MediaMsg) => (m?.mediaMime || "").startsWith("video/");

  const hasXsalsaEnvelope = (m: MediaMsg) => {
    const hasNonce = !!(m.fileNonceB64 || m.mediaIv);
    const hasEnv = !!(m.envForRecipient || m.envForSender || m.mediaKeyEnvelope || m.mediaKeySelfEnvelope);
    return hasNonce && hasEnv;
  };

  const imageUrlFor = (m: MediaMsg) => {
    const mm = (m.mediaMime || "").toLowerCase();
    const media = m.media || "";
    if (mm.startsWith("image/") && m.decryptedMediaUrl) return m.decryptedMediaUrl;
    if (mm.startsWith("image/") && media) return media;
    if (!mm && media && looksLikeImageUrl(media)) return media;
    return "";
  };

  // Video Stop when leaving presentation
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pausedGridRef = useRef<Set<HTMLVideoElement>>(new Set());

  const pauseGridVideos = useCallback(() => {
    const scope = rootRef.current;
    if (!scope) return;
    const vids = scope.querySelectorAll<HTMLVideoElement>('video[data-grid-video="1"]');
    vids.forEach((v) => {
      try {
        if (!v.paused && v.readyState > 2) pausedGridRef.current.add(v);
        v.pause();
      } catch {}
    });
  }, []);

  const resumeGridVideos = useCallback(() => {
    pausedGridRef.current.forEach((v) => {
      try {
        v.play().catch(() => {});
      } catch {}
    });
    pausedGridRef.current.clear();
  }, []);

  useEffect(() => {
    if (viewerIndex !== null) pauseGridVideos();
    else resumeGridVideos();
  }, [viewerIndex, pauseGridVideos, resumeGridVideos]);

  // ---- dims helpers (fixes undefined imgDims/vidDims/isLandscapeNow/handleImgLoad/handleVideoMeta) ----
  const [imgDims, setImgDims] = useState<Record<number, { w: number; h: number }>>({});
  const [vidDims, setVidDims] = useState<Record<number, { w: number; h: number }>>({});

  const handleImgLoad = (index: number, e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    const w = el.naturalWidth || 0;
    const h = el.naturalHeight || 0;
    if (w && h) setImgDims((p) => ({ ...p, [index]: { w, h } }));
  };

  const handleVideoMeta = (index: number) => (e: React.SyntheticEvent<HTMLVideoElement>) => {
    const el = e.currentTarget;
    const w = el.videoWidth || 0;
    const h = el.videoHeight || 0;
    if (w && h) setVidDims((p) => ({ ...p, [index]: { w, h } }));
  };

  const isLandscapeNow = (index: number, isVideo: boolean) => {
    const d = (isVideo ? vidDims[index] : imgDims[index]) || null;
    if (!d) return true; // default landscape-ish
    return d.w >= d.h;
  };

  // ---- fetch media (fixes undefined setItems/setLoaded etc.) ----
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    setViewerIndex(null);
    setLoaded(false);
    setItems((prev) => {
      revokeUrls(prev);
      return [];
    });

    (async () => {
      try {
        // Album manifest + R2 URLs path
        if (Array.isArray(albumItems) && albumItems.length) {
          const list: MediaMsg[] = albumItems.map((it) => {
            const mime = it.type === "image" ? "image/jpeg" : it.type === "video" ? "video/mp4" : undefined;
            const enc = it.crypto || {};
            const nonceB64 = enc.fileNonceB64 || enc.ivB64u || "";
            const hasNonce = !!nonceB64;

            const needDecrypt = !!(hasNonce && (enc.mediaKeyEnvelope || enc.mediaKeySelfEnvelope));

            return {
              _id: String(it.id),
              messageId: looksLikeMongoId(parentId) ? String(parentId) : undefined, // ✅ closes parent
              media: it.r2?.url || "",
              mediaMime: mime,
              r2Url: it.r2?.url || null,
              fileNonceB64: hasNonce ? nonceB64 : null,
              envForRecipient: enc.mediaKeyEnvelope || null,
              envForSender: enc.mediaKeySelfEnvelope || null,
              scheme: enc.scheme === "aes-gcm" || enc.scheme === "xsalsa20poly1305" ? enc.scheme : null,
              mediaIv: hasNonce ? nonceB64 : undefined,
              mediaKeyEnvelope: enc.mediaKeyEnvelope,
              mediaKeySelfEnvelope: enc.mediaKeySelfEnvelope,
              decryptedMediaUrl: needDecrypt ? undefined : it.r2?.url,
            };
          });

          if (!cancelled) {
            setItems(list);
            setLoaded(true);
          }
          return;
        }

        // legacy fetch
        let res: any;
        if (onlyIds.length) {
          res = await api.get(`/message/media/by-ids`, { params: { ids: onlyIds.join(",") } });
        } else {
          res = await api.get(`/message/thread-media/${encodeURIComponent(userId)}`, { params: { limit: 80 } });
        }

        const data = res.data;
        const serverItems = Array.isArray(data) ? data : data?.items || [];
        const rawList: MediaMsg[] = serverItems.map((m: any) => ({ ...m }));

        let list = rawList;
        if (onlyIds.length) {
          const wanted = new Set(onlyIds.map(String));
          const order = new Map(onlyIds.map((id, i) => [String(id), i]));
          list = rawList
            .filter((m) => wanted.has(String(m._id)))
            .sort((a, b) => (order.get(String(a._id)) ?? 0) - (order.get(String(b._id)) ?? 0));
        }

        if (!cancelled) {
          setItems(list);
          setLoaded(true);
        }
      } catch (e) {
        console.error("Failed to load thread media:", e);
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, JSON.stringify(onlyIds), JSON.stringify(albumItems), isSender, parentId]);

  // ---- decrypt helpers (fixes undefined ensurePlayableAt/inFlight) ----
  const inFlight = useRef<Set<string>>(new Set());

  const ensurePlayableAt = useCallback(
    async (index: number) => {
      const m = items[index];
      if (!m) return;

      // already OK
      if (m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__") return;

      const isR2 = !!(m.r2Url && (m.fileNonceB64 || m.mediaIv) && (m.envForRecipient || m.envForSender));
      const xsalsa = hasXsalsaEnvelope(m);

      // xsalsa/aes-gcm R2 item (or xsalsa legacy)
      if (xsalsa) {
        const k = `${isR2 ? "r2" : "xsalsa"}:${msgKey(m)}`;
        if (inFlight.current.has(k)) return;
        inFlight.current.add(k);
        try {
          const preferSender = isSender;
          const url = await decryptXsalsaItemToBlobUrl(m, preferSender, isR2 ? null : token || null);
          setItems((prev) => {
            const copy = [...prev];
            const mm = { ...copy[index], decryptedMediaUrl: url };
            copy[index] = mm;
            return copy;
          });
        } catch (e) {
          console.warn("[MediaPresentation] XSALSA decrypt failed:", e);
          setItems((prev) => {
            const copy = [...prev];
            copy[index] = { ...copy[index], decryptedMediaUrl: "__FAIL__" };
            return copy;
          });
        } finally {
          inFlight.current.delete(k);
        }
        return;
      }

      // legacy AES-GCM decryptMedia path
      if ((m.mediaMime || "").startsWith("video/") || (m.mediaMime || "").startsWith("image/")) {
        const k = `legacy:${msgKey(m)}`;
        if (inFlight.current.has(k)) return;
        inFlight.current.add(k);
        try {
          const copy = [...items];
          await decryptMedia(copy[index] as any, token, user?._id);
          setItems(copy);
        } finally {
          inFlight.current.delete(k);
        }
      }
    },
    [items, isSender, token, user?._id]
  );

  // Pre-decrypt a few when grid is shown
  useEffect(() => {
    if (!items.length || viewerIndex !== null) return;
    (async () => {
      for (let i = 0; i < Math.min(items.length, 6); i++) {
        await ensurePlayableAt(i);
      }
    })();
  }, [items, viewerIndex, ensurePlayableAt]);

  // ---- expire handling navigation back (fixes undefined markExpiredOnceRef etc.) ----
  const markExpiredOnceRef = useRef(false);
  const onlyIdsKey = useMemo(() => onlyIds.join(","), [onlyIds]);

  useEffect(() => {
    if (markExpiredOnceRef.current) return;
    if (!onlyIds.length) return;
    if (!loaded) return;
    if (items.length > 0) return;

    markExpiredOnceRef.current = true;
    const payload = { expired: { parentId, ids: onlyIds } };

    if (userId) {
      nav(`/chat/${encodeURIComponent(userId)}`, { replace: true, state: { toUserId: userId, ...payload } });
    } else {
      nav("/chat", { replace: true, state: payload });
    }
  }, [onlyIdsKey, loaded, items.length, parentId, userId, nav]);

  // ---- overlay open (fixes undefined buildOverlayItems/openOverlay usage) ----
  const buildOverlayItems = (arr: MediaMsg[]) => {
    // We skip "stream" iframe videos for overlay, keep them inline.
    return arr
      .filter((m) => !isStream(m))
      .map((m) => {
        const isVid = isVideoMime(m);
        const raw = isVid ? (m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) : imageUrlFor(m);
        const url = raw?.startsWith("blob:") && isVid ? `${raw}#ext=.mp4` : raw;
        return { url, type: isVid ? "video" : "image" };
      })
      .filter((x) => !!x.url);
  };

  const onOpenOverlay = useCallback(
    async (index: number) => {
      const m = items[index];
      if (!m) return;
      if (isStream(m)) return;

      setViewerIndex(index);
      pauseGridVideos();
      openedIdsRef.current.add(String(m.messageId || m._id));

      await ensurePlayableAt(index);

      const viewerItems = buildOverlayItems(items);

      // Try to map clicked item to overlay list by URL
      const clickedUrl = (isVideoMime(m) ? (m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) : imageUrlFor(m)) || "";
      const normalizedClicked = clickedUrl?.startsWith("blob:") ? `${clickedUrl}#ext=.mp4` : clickedUrl;

      const start = Math.max(
        0,
        viewerItems.findIndex((vi: any) => vi.url === normalizedClicked || vi.url.replace(/#ext=.mp4$/, "") === clickedUrl)
      );

      openOverlay(viewerItems, start === -1 ? 0 : start);
    },
    [items, ensurePlayableAt, openOverlay, pauseGridVideos]
  );

  // socket: remove expired items
  useEffect(() => {
    if (!socket) return;
    const onExpired = (p: any) => {
      const ids: string[] = Array.isArray(p?.ids) ? p.ids.map(String) : [];
      if (!ids.length) return;
      setItems((prev) => {
        prev.forEach((m) => {
          if (ids.includes(String(m._id)) && m.decryptedMediaUrl?.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(m.decryptedMediaUrl);
            } catch {}
          }
        });
        return prev.filter((m) => !ids.includes(String(m._id)));
      });
    };
    socket.on("message_expired", onExpired);
    return () => socket.off("message_expired", onExpired);
  }, [socket]);

  // socket: hard-deletes
  useEffect(() => {
    if (!socket) return;
    const onDeleted = ({ messageId }: { messageId: string }) => {
      setItems((prev) => prev.filter((m) => String(m._id) !== String(messageId)));
    };
    socket.on("message_deleted", onDeleted);
    return () => socket.off("message_deleted", onDeleted);
  }, [socket]);

  // Inject helper CSS once
  useEffect(() => {
    const id = "cmp-scrollbar-utils";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.textContent = `
        .no-scrollbars { -ms-overflow-style: none; scrollbar-width: none; }
        .no-scrollbars::-webkit-scrollbar { width: 0; height: 0; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // ---- layout helpers (same logic, now defined) ----
  const isSingle = viewerIndex === null && items.length === 1;
  const isTwo = viewerIndex === null && items.length === 2;

  type Chunk = { kind: "portrait" | "landscape"; indices: number[] };
  const chunks: Chunk[] = [];
  if (!isSingle && !isTwo && items.length) {
    let cur: Chunk | null = null;
    items.forEach((m, index) => {
      const video = isVideoMime(m) || isStream(m);
      const land = isLandscapeNow(index, video);
      const kind: Chunk["kind"] = land ? "landscape" : "portrait";
      if (!cur || cur.kind !== kind) {
        cur = { kind, indices: [index] };
        chunks.push(cur);
      } else {
        cur.indices.push(index);
      }
    });
  }

  if (!userId) return null;

  return (
    <div
      ref={rootRef}
      className={isSingle ? "no-scrollbars" : undefined}
      style={{
        ...styles.container,
        ...(isSingle ? styles.noScrollContainer : styles.noHScrollContainer),
      }}
      onWheel={isSingle ? (e) => e.preventDefault() : undefined}
      onTouchMove={isSingle ? (e) => e.preventDefault() : undefined}
    >
      {viewerIndex === null && (
        <button onClick={safeBack} style={styles.exitButton}>
          ✖
        </button>
      )}

      {/* Single */}
      {isSingle ? (
        <div style={styles.singleWrap}>
          {(() => {
            const m = items[0];
            const isVid = isVideoMime(m) || isStream(m);

            if (isStream(m)) {
              return (
                <iframe
                  src={m.media}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  style={{ width: "55%", maxWidth: 800, aspectRatio: "16 / 9", border: 0, borderRadius: 12, background: "#000" }}
                  title={t("chatMedia.videoTitle")}
                />
              );
            }

            if (isVid) {
              const videoUrl =
                (m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) || "";
              if (videoUrl) {
                return (
                  <SmartVideoPlayerChat
                    videoUrl={videoUrl}
                    controls
                    muted
                    loop
                    preload="auto"
                    autoPlayWhenVisible={false}
                    noVisibilityPause
                    videoProps={{
                      autoPlay: true,
                      "data-grid-video": "1",
                      onLoadedMetadata: handleVideoMeta(0),
                    }}
                    containerStyle={{ borderRadius: 12 }}
                    videoStyle={{
                      borderRadius: 12,
                      objectFit: "contain",
                      width: "55%",
                      maxWidth: 800,
                      height: "auto",
                    }}
                  />
                );
              }
            }

            const url = imageUrlFor(m);
            return url ? (
              <img src={url} alt="" style={styles.singleMedia} onLoad={(e) => handleImgLoad(0, e)} />
            ) : (
              <div style={styles.singlePlaceholder}>🎬</div>
            );
          })()}
        </div>
      ) : isTwo ? (
        // Two-up
        <div style={styles.twoUpGrid}>
          {[0, 1].map((index) => {
            const m = items[index];
            const isVid = isVideoMime(m) || isStream(m);
            const land = isLandscapeNow(index, isVid);
            const dims = isVid ? vidDims[index] : imgDims[index];
            const arStyle: React.CSSProperties = land ? (dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : { aspectRatio: "16 / 9" }) : {};

            return (
              <div
                key={m._id}
                style={{ ...styles.mediaBox, ...styles.twoUpCell, ...arStyle, position: "relative" }}
                onClick={() => onOpenOverlay(index)}
              >
                {isStream(m) ? (
                  <iframe
                    src={m.media}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    style={{ width: "100%", height: "100%", border: 0, borderRadius: 12, background: "#000" }}
                    title="Video"
                  />
                ) : isVid ? (
                  <SmartVideoPlayerChat
                    videoUrl={(m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) || ""}
                    controls
                    muted
                    loop
                    preload="metadata"
                    autoPlayWhenVisible
                    videoProps={{ "data-grid-video": "1", onLoadedMetadata: handleVideoMeta(index) }}
                    containerStyle={{ borderRadius: 12, width: "100%", height: "100%" }}
                    videoStyle={land ? styles.landMediaFill : styles.twoUpPortraitContain}
                  />
                ) : land ? (
                  <img src={imageUrlFor(m)} alt="" onLoad={(e) => handleImgLoad(index, e)} style={styles.landMediaFill} />
                ) : (
                  <img src={imageUrlFor(m)} alt="" onLoad={(e) => handleImgLoad(index, e)} style={styles.twoUpPortraitContain} />
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // Multi
        <div style={styles.gridOuter}>
          <div style={styles.grid}>
            {chunks.map((chunk, ci) => {
              if (chunk.kind === "portrait") {
                return (
                  <div key={`p-${ci}`} style={styles.columns}>
                    {chunk.indices.map((index) => {
                      const m = items[index];
                      const isVid = isVideoMime(m) || isStream(m);
                      return (
                        <div key={m._id} style={{ ...styles.mediaBox, position: "relative" }} onClick={() => onOpenOverlay(index)}>
                          {isStream(m) ? (
                            <iframe
                              src={m.media}
                              allow="autoplay; fullscreen; picture-in-picture"
                              allowFullScreen
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              style={{ width: "100%", aspectRatio: "16 / 9", border: 0, borderRadius: 12, background: "#000" }}
                              title="Video"
                            />
                          ) : isVid ? (
                            <SmartVideoPlayerChat
                              videoUrl={(m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) || ""}
                              controls
                              loop
                              preload="metadata"
                              autoPlayWhenVisible
                              suppressPauseNearEndSec={2}
                              pauseDebounceMs={350}
                              visibilityRootMargin="800px 0px"
                              videoProps={{ "data-grid-video": "1", onLoadedMetadata: handleVideoMeta(index) }}
                              containerStyle={{ borderRadius: 12 }}
                              videoStyle={{ borderRadius: 12, objectFit: "contain", width: "90%", height: "auto" }}
                            />
                          ) : (
                            <img src={imageUrlFor(m)} alt="" onLoad={(e) => handleImgLoad(index, e)} style={styles.thumb} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }

              // landscape run row
              return (
                <div key={`l-${ci}`} style={styles.landRowGrid}>
                  {chunk.indices.map((index) => {
                    const m = items[index];
                    const isVid = isVideoMime(m) || isStream(m);
                    const dims = isVid ? vidDims[index] : imgDims[index];
                    const arStyle: React.CSSProperties = dims ? { aspectRatio: `${dims.w} / ${dims.h}` } : { aspectRatio: "16 / 9" };

                    return (
                      <div key={m._id} style={{ ...styles.mediaBox, ...arStyle, position: "relative" }} onClick={() => onOpenOverlay(index)}>
                        {isStream(m) ? (
                          <iframe
                            src={m.media}
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            style={{ width: "100%", height: "100%", border: 0, borderRadius: 12, background: "#000" }}
                            title="Video"
                          />
                        ) : isVid ? (
                          <SmartVideoPlayerChat
                            videoUrl={(m.decryptedMediaUrl && m.decryptedMediaUrl !== "__FAIL__" ? m.decryptedMediaUrl : m.media) || ""}
                            controls
                            loop
                            preload="metadata"
                            autoPlayWhenVisible
                            suppressPauseNearEndSec={2}
                            pauseDebounceMs={350}
                            visibilityRootMargin="800px 0px"
                            videoProps={{ "data-grid-video": "1", onLoadedMetadata: handleVideoMeta(index) }}
                            containerStyle={{ borderRadius: 12, width: "100%", height: "100%" }}
                            videoStyle={styles.landMediaFill}
                          />
                        ) : (
                          <img src={imageUrlFor(m)} alt="" onLoad={(e) => handleImgLoad(index, e)} style={styles.landMediaFill} />
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}

            {items.length === 0 && <div style={{ color: "#888", padding: "16px 0" }}>{t("chatMedia.noMedia")}</div>}
          </div>
        </div>
      )}

      {Overlay}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { backgroundColor:'#000', minHeight:'100vh', padding:'30px', boxSizing:'border-box', position:'relative', color:'#fff',  },
 
  viewerOverlay: {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.97)', zIndex: 9999,
    display:'flex', justifyContent:'center', alignItems:'center'
  },
  viewerMedia: { maxWidth:'90%', maxHeight:'90%', objectFit:'contain' },
  exitButton: {
    position:'absolute', top: 15, right: 26, background:'rgba(255,255,255,0.2)',
    color:'#fff', border:'none', fontSize:'14px', padding:'8px', cursor:'pointer',
    borderRadius:'50%', zIndex: 10000,
  },
  viewerCloseBtn: {
    position:'fixed', top:14, right:21, background:'rgba(35,12,12,0.55)',
    color:'#fff', border:'none', fontSize:16, padding:'10px 12px',
    cursor:'pointer', borderRadius:9999,
  },
  viewerNavBtn: {
    position:'fixed', top:'50%', transform:'translateY(-50%)', background:'rgba(0,0,0,0.55)',
    color:'#fff', border:'none', fontSize:28, padding:'10px 14px', cursor:'pointer', borderRadius:10, zIndex: 10001,
  },

  singleWrap: {
    minHeight: 'calc(100vh - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  singleMedia: {
    maxWidth: '92vw',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: 12,
    display: 'block',
    boxShadow: '0 2px 12px rgba(0,0,0,0.6)',
  },
  singlePlaceholder: {
    width: 'min(92vw, 1000px)',
    height: 'min(85vh, 70vw)',
    background:'#1a1a1a',
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    color:'#999',
    fontSize: 48,
    borderRadius: 12,
  },
  singleVideoContainer: {
    width: 'min(92vw, 1000px)',
    maxHeight: '85vh',
    borderRadius: 12,
  },
  singleVideo: {
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    borderRadius: 12,
  },

  // Grid placeholder (used above)
  thumbPlaceholder: {
    width: '100%',
    height: 200,
    background:'#1a1a1a',
    display:'flex',
    alignItems:'center',
    justifyContent:'center',
    color:'#999',
    borderRadius: 12,
  },

  noHScrollContainer: {
    overflowX: 'hidden',
    overflowY: 'auto',
    // allow only vertical touch panning; prevents horizontal swipe/drag
    touchAction: 'pan-y',
  },

  noScrollContainer: {
    height: '100vh',
    overflow: 'hidden',          // no scrollbars (plus class hides them visually)
    overscrollBehavior: 'none',  // avoids rubber-banding on single
    touchAction: 'none',         // blocks touch scrolling in any direction
  },

  gridOuter: {
    width: '100%',
    overflowX: 'hidden',
    contain: 'content',
  },
  grid: { paddingTop: '2%' }, // (no columnCount here; portrait chunks will have their own)
  columns: { columnCount: 3, columnGap: '16px', paddingTop: '2%' },
  
  landRowGrid: {
    display: 'grid',
    gridAutoFlow: 'row',
    gap: 16,
    paddingTop: '2%',
    gridTemplateColumns: 'repeat(auto-fill, minmax(520px, 1fr))',
  },
  
  mediaBox: {
    display: 'inline-block',
    width: '100%',
    marginBottom: '16px',
    breakInside: 'avoid',
    borderRadius: '12px',
    overflow: 'hidden',
    backgroundColor: 'transparent',
    boxShadow: '0 2px 8px rgba(0,0,0,0.6)',
    marginTop: '2%',
  },
  
  thumb: { width: '100%', height: 'auto', display: 'block', borderRadius: '12px' },
  landMediaFill: { width: '100%', height: '100%', display: 'block', objectFit: 'cover', borderRadius: 12 },
  
  twoUpGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
    height: 'calc(100vh - 60px)',
    alignItems: 'center',
    overflow: 'hidden',
    paddingTop: '2%',
  },
  twoUpCell: { display: 'block', marginBottom: 0 },
  twoUpPortraitContain: { width: '100%', height: '100%', objectFit: 'contain', display: 'block', borderRadius: 12 },
  
};

